import { ConnectorUpdate } from '@web3-react/types';
import { AbstractConnector } from '@web3-react/abstract-connector';
// tslint:disable-next-line
import type EthereumProvider from '@walletconnect/ethereum-provider';
import { SupportedChains } from '../../config/constants';
// import { EthereumRpcMap } from '@walletconnect/ethereum-provider/dist/types/EthereumProvider';

export interface IRPCMap {
    [chainId: number]: string;
}

interface WallectConnectV2Args {
    rpcMap: any;
    projectId: string;
    chainId: SupportedChains;
    supportedChainIds: number[];
    bridge: string;
}

export class WalletConnectV2Connector extends AbstractConnector {
    provider?: typeof EthereumProvider.prototype;
    bridge: string;
    chainId: SupportedChains;
    projectId: string;
    rpcMap: any;

    constructor(args: WallectConnectV2Args) {
        const { bridge, chainId, projectId, rpcMap, supportedChainIds } = args;
        super({ supportedChainIds: supportedChainIds });
        this.bridge = bridge;
        this.chainId = chainId;
        this.projectId = projectId;
        this.rpcMap = rpcMap;
    }

    static clearStorage = (storage: Storage) => {
        storage.removeRegExp(new RegExp('^wc@2:'));
    };

    activate = async (): Promise<ConnectorUpdate<string | number>> => {
        const provider = await import('@walletconnect/ethereum-provider').then(
            (module: any) => {
                return module.default.init({
                    projectId: this.projectId,
                    rpcMap: this.rpcMap,
                    chains: this.supportedChainIds,
                    optionalChains: [0],
                    showQrModal: true,
                    disableProviderPing: true,
                    // Methods and events based on what is used on the decentraland dapps and the ethereum-provider lib found at:
                    // https://github.com/WalletConnect/walletconnect-monorepo/blob/v2.0/providers/ethereum-provider/src/constants/rpc.ts
                    // If the wallet doesn't support non optional methods, it will not allow the connection.
                    methods: ['eth_sendTransaction', 'personal_sign'],
                    optionalMethods: [
                        'eth_accounts',
                        'eth_requestAccounts',
                        'eth_sign',
                        'eth_signTypedData_v4',
                        'wallet_switchEthereumChain',
                        'wallet_addEthereumChain',
                    ],

                    events: ['chainChanged', 'accountsChanged'],
                    optionalEvents: ['disconnect'],
                });
            }
        );

        const accounts = await provider.enable();

        provider.on('accountsChanged', this.handleAccountsChanged);
        provider.on('chainChanged', this.handleChainChanged);
        provider.on('disconnect', this.handleDisconnect);

        this.provider = provider;

        return {
            chainId: provider.chainId,
            account: accounts[0],
            provider,
        };
    };

    getProvider = async (): Promise<any> => {
        if (!this.provider) {
            throw new Error('Provider is undefined');
        }

        return this.provider;
    };

    getChainId = async (): Promise<string | number> => {
        if (!this.provider) {
            throw new Error('Provider is undefined');
        }

        return this.provider.chainId;
    };

    getAccount = async (): Promise<string | null> => {
        if (!this.provider) {
            throw new Error('Provider is undefined');
        }

        return this.provider.accounts[0];
    };

    getWalletName = (): string | undefined => {
        return this.provider?.session?.peer.metadata.name;
    };

    deactivate = (): void => {
        if (!this.provider) {
            return;
        }

        this.emitDeactivate();

        this.provider
            .removeListener('accountsChanged', this.handleAccountsChanged)
            .removeListener('chainChanged', this.handleChainChanged)
            .removeListener('disconnect', this.handleDisconnect)
            .disconnect();
    };

    handleAccountsChanged = (accounts: string[]): void => {
        this.emitUpdate({ account: accounts[0] });
    };

    handleChainChanged = (chainId: string | number): void => {
        this.emitUpdate({ chainId });
    };

    handleDisconnect = (): void => {
        if (!this.provider) {
            throw new Error('Provider is undefined');
        }

        this.deactivate();
    };
}
